import { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Calendar, ChevronDown } from 'lucide-react';
import { useFilterStore } from '../../store/filterStore';
import dayjs from 'dayjs';
import 'react-datepicker/dist/react-datepicker.css';

const presets = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last7days' },
  { label: 'Last 30 Days', value: 'last30days' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Year', value: 'thisYear' },
  { label: 'Custom', value: 'custom' },
];

export default function DateRangePicker({
  startDate: propStartDate,
  endDate: propEndDate,
  onChange
} = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [localPreset, setLocalPreset] = useState('last7days');
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const dropdownRef = useRef(null);

  const filterStore = useFilterStore();

  // Use props if provided, otherwise use global store
  const useLocalState = onChange !== undefined;
  const startDate = useLocalState ? propStartDate : filterStore.startDate;
  const endDate = useLocalState ? propEndDate : filterStore.endDate;
  const datePreset = useLocalState ? localPreset : filterStore.datePreset;

  const setDateRange = useLocalState
    ? (start, end) => onChange(start, end)
    : filterStore.setDateRange;

  const setDatePreset = useLocalState
    ? (preset) => {
        setLocalPreset(preset);
        const today = dayjs();
        let start, end;
        switch (preset) {
          case 'today':
            start = end = today.format('YYYY-MM-DD');
            break;
          case 'yesterday':
            start = end = today.subtract(1, 'day').format('YYYY-MM-DD');
            break;
          case 'last7days':
            start = today.subtract(6, 'day').format('YYYY-MM-DD');
            end = today.format('YYYY-MM-DD');
            break;
          case 'last30days':
            start = today.subtract(29, 'day').format('YYYY-MM-DD');
            end = today.format('YYYY-MM-DD');
            break;
          case 'thisMonth':
            start = today.startOf('month').format('YYYY-MM-DD');
            end = today.format('YYYY-MM-DD');
            break;
          case 'lastMonth':
            start = today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
            end = today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
            break;
          case 'thisYear':
            start = today.startOf('year').format('YYYY-MM-DD');
            end = today.format('YYYY-MM-DD');
            break;
          default:
            return;
        }
        onChange(start, end);
      }
    : filterStore.setDatePreset;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetClick = (preset) => {
    setDatePreset(preset);
    if (preset === 'custom') {
      // Initialize selection with current dates, user can then pick new range
      setSelectionStart(startDate ? new Date(startDate) : null);
      setSelectionEnd(endDate ? new Date(endDate) : null);
      // Don't close dropdown for custom - let user pick dates
      return;
    }
    setIsOpen(false);
  };

  const handleCustomDateChange = (dates) => {
    const [start, end] = dates;
    // Track the selection in local state
    setSelectionStart(start);
    setSelectionEnd(end);

    // Only update the actual date range when both dates are selected
    if (start && end) {
      const startStr = dayjs(start).format('YYYY-MM-DD');
      const endStr = dayjs(end).format('YYYY-MM-DD');
      if (useLocalState) {
        setLocalPreset('custom');
        onChange(startStr, endStr);
      } else {
        filterStore.setDateRange(startStr, endStr, 'custom');
      }
    }
  };

  const displayLabel =
    presets.find((p) => p.value === datePreset)?.label || 'Custom';

  const formattedRange = `${dayjs(startDate).format('MMM D, YYYY')} - ${dayjs(
    endDate
  ).format('MMM D, YYYY')}`;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
      >
        <Calendar className="w-4 h-4 text-secondary-500" />
        <span className="text-sm font-medium text-secondary-700">
          {displayLabel}
        </span>
        <span className="text-sm text-secondary-500">{formattedRange}</span>
        <ChevronDown className="w-4 h-4 text-secondary-400" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-secondary-200 z-50 animate-fadeIn">
          {/* Presets */}
          <div className="p-2 border-b border-secondary-200">
            <div className="grid grid-cols-2 gap-1">
              {presets.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handlePresetClick(preset.value)}
                  className={`px-3 py-2 text-sm rounded-lg text-left transition-colors ${
                    datePreset === preset.value
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-secondary-600 hover:bg-secondary-50'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Picker */}
          {datePreset === 'custom' && (
            <div className="p-4">
              <DatePicker
                selectsRange
                startDate={selectionStart}
                endDate={selectionEnd}
                onChange={handleCustomDateChange}
                inline
                monthsShown={1}
              />
            </div>
          )}

          {/* Apply button for custom */}
          {datePreset === 'custom' && (
            <div className="p-2 border-t border-secondary-200">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full btn btn-primary"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
